/**
 * CPA-ONBOARDING-1 — firm-profile shapes and helpers.
 *
 * Split out of FirmProfilePage.tsx to keep every file under the 400-line
 * ceiling in docs/FILE_SIZE_POLICY.md.
 *
 * ATTEST-DEPLOY-UNIT-1 R2 prep 3/3 — FirmProfile and FirmProfileResponse now
 * live in `@/api/sharedTypes`: Attest's SigningIdentityCard reads the same
 * firm profile this page edits, a genuinely cross-product concept. Re-exported
 * below so every existing `@/pages/firmProfile/model` import keeps working
 * unchanged. FormState/UpdateField/EMPTY_FORM/toForm/extractErrors are
 * QoE-page-only (the editable form subset and its helpers) and stay here.
 */
import { formatApiError } from '@/utils/httpErrors'
import type { FirmProfile, FirmProfileResponse } from '@/api/sharedTypes'

export type { FirmProfile, FirmProfileResponse }

/** The editable subset — mirrors _EDITABLE_FIELDS in core/db/firm_profile_service.py. */
export interface FormState {
  firm_legal_name: string
  firm_dba: string
  firm_website: string
  firm_email: string
  cpa_name: string
  cpa_license_no: string
  cpa_license_state: string
  firm_address_street: string
  firm_address_city: string
  firm_address_state: string
  firm_address_zip: string
  firm_logo_ref: string
  engagement_code_prefix: string
  disclaimer_boilerplate: string
}

export type UpdateField = (field: keyof FormState, value: string) => void

export const EMPTY_FORM: FormState = {
  firm_legal_name: '',
  firm_dba: '',
  firm_website: '',
  firm_email: '',
  cpa_name: '',
  cpa_license_no: '',
  cpa_license_state: '',
  firm_address_street: '',
  firm_address_city: '',
  firm_address_state: '',
  firm_address_zip: '',
  firm_logo_ref: '',
  engagement_code_prefix: '',
  disclaimer_boilerplate: '',
}

export function toForm(profile: FirmProfile | null): FormState {
  if (!profile) return { ...EMPTY_FORM }
  const next = { ...EMPTY_FORM }
  for (const key of Object.keys(EMPTY_FORM) as (keyof FormState)[]) {
    next[key] = profile[key] ?? ''
  }
  return next
}

/**
 * The firm-profile routes answer a failed save with `detail: string[]` — one
 * readable line per problem ("CPA license number is required before the profile
 * can be activated"). formatApiError only understands a string detail, so a
 * list would collapse to a bare "422 - Unprocessable Entity", which tells the
 * CPA nothing about which field to fix. Pull the list out first.
 */
export function extractErrors(err: unknown): string[] {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (Array.isArray(detail)) {
    const lines = detail.map(d => (typeof d === 'string' ? d : JSON.stringify(d))).filter(Boolean)
    if (lines.length) return lines
  }
  return [formatApiError(err)]
}
